/**
 * More than one business on one device, for a person who works for a group (a chain, a holding, an accountant
 * with several clients): each business keeps its own sign-in, and the profile menu switches between them.
 *
 * Nothing changes on the server: every business is still a separate sign-in with its own user, PIN and
 * permissions. The device only keeps each business's own local state (its sign-in token, who is signed in, the
 * till, location and kitchen station of this device, the offline till, a few dismissed notices) apart, and swaps
 * the whole set when the person switches. A business's data never reaches another one's screen or requests.
 */

/** Everything on this device that belongs to one business's sign-in. */
export const BUSINESS_KEYS = [
  'erp.token',
  'erp.me',
  'erp.tenant',
  'erp.register',
  'erp.location',
  'erp.station',
  'erp.reader',
  'erp.offline.snapshot',
  'erp.offline.sales',
  'erp.plan.banner',
  'erp.announcements.closed',
  'erp.calendar.prefs',
  'erp.calendar.dismissed',
] as const;

const PREFIX = 'erp.account.';

export interface Account {
  /** `tenantId:userId` */
  key: string;
  business: string;
  slug: string;
  user: string;
}
interface Stored {
  account: Account;
  values: Record<string, string>;
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;
const store = (): Store | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

const json = <T>(raw: string | null): T | null => {
  try {
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

/** The business signed in on screen now, if any. */
export function currentAccount(s: Store | null = store()): Account | null {
  if (!s) return null;
  const me = json<{ tenantId: string; userId: string; name: string }>(s.getItem('erp.me'));
  const tenant = json<{ slug: string; name: string }>(s.getItem('erp.tenant'));
  if (!me || !tenant || !s.getItem('erp.token')) return null;
  return { key: `${me.tenantId}:${me.userId}`, business: tenant.name, slug: tenant.slug, user: me.name };
}

/** The other businesses kept on this device, by name. */
export function otherAccounts(s: Store | null = store()): Account[] {
  if (!s) return [];
  const here = currentAccount(s)?.key;
  const out: Account[] = [];
  for (let i = 0; i < s.length; i++) {
    const k = s.key(i);
    if (!k?.startsWith(PREFIX)) continue;
    const v = json<Stored>(s.getItem(k));
    if (v?.account && v.account.key !== here) out.push(v.account);
  }
  return out.sort((a, b) => a.business.localeCompare(b.business) || a.user.localeCompare(b.user));
}

/** Sales made offline that have not reached the server yet: they must be sent before leaving the business. */
export function unsentSales(s: Store | null = store()): number {
  const q = json<{ receipt_no?: number; error?: string }[]>(s?.getItem('erp.offline.sales') ?? null) ?? [];
  return q.filter((x) => x.receipt_no === undefined).length;
}

/** Keeps the business on screen aside, so the device can sign in to or open another one. */
export function stashCurrent(s: Store | null = store()): boolean {
  const account = currentAccount(s);
  if (!s || !account) return false;
  const values: Record<string, string> = {};
  for (const k of BUSINESS_KEYS) {
    const v = s.getItem(k);
    if (v !== null) values[k] = v;
  }
  s.setItem(PREFIX + account.key, JSON.stringify({ account, values } satisfies Stored));
  for (const k of BUSINESS_KEYS) s.removeItem(k);
  return true;
}

/** Puts a kept business back on screen (after the current one was stashed or signed out). */
export function restore(key: string, s: Store | null = store()): boolean {
  const v = json<Stored>(s?.getItem(PREFIX + key) ?? null);
  if (!s || !v) return false;
  for (const k of BUSINESS_KEYS) s.removeItem(k);
  for (const [k, val] of Object.entries(v.values)) if ((BUSINESS_KEYS as readonly string[]).includes(k)) s.setItem(k, val);
  s.removeItem(PREFIX + key);
  return true;
}

/** Forgets a kept business on this device; gives back its sign-in token so the caller can end it on the server. */
export function forget(key: string, s: Store | null = store()): string | null {
  const v = json<Stored>(s?.getItem(PREFIX + key) ?? null);
  s?.removeItem(PREFIX + key);
  return v?.values['erp.token'] ?? null;
}

/** After a sign-in: a kept copy of the same business and person is replaced by the fresh one. */
export function dropStaleCopy(s: Store | null = store()) {
  const here = currentAccount(s);
  if (here) s?.removeItem(PREFIX + here.key);
}

/** Opens a business on screen: the page loads again, so nothing of the one before stays in memory. */
export function go(path = '/home') {
  window.location.assign(path);
}
